/**
 * Flat Message Reducer Actions
 *
 * Write atoms for managing the flat message-based chat state:
 * - Message CRUD (add, update, remove, bulk operations)
 * - Execution lifecycle (complete, fail)
 * - Clearing session responses for re-runs
 * - Context-aware atoms (auto-inject loadableId)
 *
 * @module chat/messageReducer
 */

import type {SimpleChatMessage} from "@agenta/shared/types"
import {generateId} from "@agenta/shared/utils"
import {atom} from "jotai"

import {derivedLoadableIdAtom} from "../execution/selectors"
import {buildUserMessage} from "../helpers/messageFactory"
import {syncChatMessagesToEntityAtom} from "../helpers/syncChatMessagesToEntity"

import {
    executionByMessageIdAtomFamily,
    messageIdsAtomFamily,
    messagesByIdAtomFamily,
} from "./messageAtoms"
import type {
    AddMessagePayload,
    AddUserMessagePayload,
    ChatMessage,
    ClearSessionResponsesPayload,
    CompleteExecutionPayload,
    DeleteMessagePayload,
    FailExecutionPayload,
    PatchMessagePayload,
    RemoveMessagesPayload,
    TruncateChatPayload,
    UpdateMessagePayload,
} from "./messageTypes"
import {SHARED_SESSION_ID} from "./messageTypes"

/**
 * Generate a unique message ID.
 */
export function generateMessageId(): string {
    return `msg-${generateId()}`
}

// ============================================================================
// MESSAGE CRUD
// ============================================================================

/**
 * Add a message to the conversation.
 *
 * If `afterMessageId` is provided, inserts after that message.
 * Otherwise appends to the end.
 */
export const addMessageAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & AddMessagePayload) => {
        const {loadableId, message, afterMessageId} = payload
        const ids = get(messageIdsAtomFamily(loadableId))
        const byId = get(messagesByIdAtomFamily(loadableId))

        // Ensure message has an ID
        const msg: ChatMessage = message.id ? message : {...message, id: generateMessageId()}
        const msgId = msg.id as string

        // Insert into ordered list
        let nextIds: string[]
        if (afterMessageId) {
            const idx = ids.indexOf(afterMessageId)
            if (idx >= 0) {
                nextIds = [...ids.slice(0, idx + 1), msgId, ...ids.slice(idx + 1)]
            } else {
                nextIds = [...ids, msgId]
            }
        } else {
            nextIds = [...ids, msgId]
        }

        set(messageIdsAtomFamily(loadableId), nextIds)
        set(messagesByIdAtomFamily(loadableId), {...byId, [msgId]: msg})

        // Sync to entity drafts (same pattern as completion mode cell edits)
        set(syncChatMessagesToEntityAtom, loadableId)

        return msgId
    },
)

/**
 * Add multiple messages in order.
 */
export const addMessagesAtom = atom(
    null,
    (get, set, payload: {loadableId: string; messages: ChatMessage[]; afterMessageId?: string}) => {
        const {loadableId, messages, afterMessageId} = payload
        if (messages.length === 0) return

        const ids = get(messageIdsAtomFamily(loadableId))
        const byId = {...get(messagesByIdAtomFamily(loadableId))}

        const newIds: string[] = []
        for (const message of messages) {
            const msg: ChatMessage = message.id ? message : {...message, id: generateMessageId()}
            const msgId = msg.id as string
            byId[msgId] = msg
            newIds.push(msgId)
        }

        let nextIds: string[]
        if (afterMessageId) {
            const idx = ids.indexOf(afterMessageId)
            if (idx >= 0) {
                nextIds = [...ids.slice(0, idx + 1), ...newIds, ...ids.slice(idx + 1)]
            } else {
                nextIds = [...ids, ...newIds]
            }
        } else {
            nextIds = [...ids, ...newIds]
        }

        set(messageIdsAtomFamily(loadableId), nextIds)
        set(messagesByIdAtomFamily(loadableId), byId)

        // Sync to entity drafts
        set(syncChatMessagesToEntityAtom, loadableId)
    },
)

/**
 * Update a message's content (partial merge).
 */
const updateMessageAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & UpdateMessagePayload) => {
        const {loadableId, messageId, updates} = payload
        const byId = get(messagesByIdAtomFamily(loadableId))
        const existing = byId[messageId]
        if (!existing) return

        set(messagesByIdAtomFamily(loadableId), {
            ...byId,
            [messageId]: {...existing, ...updates, id: existing.id},
        })

        // Sync to entity drafts
        set(syncChatMessagesToEntityAtom, loadableId)
    },
)

/**
 * Remove messages by ID.
 * Also cleans up their execution state.
 */
const removeMessagesAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & RemoveMessagesPayload) => {
        const {loadableId, messageIds: toRemove} = payload
        if (toRemove.length === 0) return

        const removeSet = new Set(toRemove)

        const ids = get(messageIdsAtomFamily(loadableId))
        set(
            messageIdsAtomFamily(loadableId),
            ids.filter((id) => !removeSet.has(id)),
        )

        const byId = {...get(messagesByIdAtomFamily(loadableId))}
        for (const id of toRemove) {
            delete byId[id]
        }
        set(messagesByIdAtomFamily(loadableId), byId)

        const execMap = {...get(executionByMessageIdAtomFamily(loadableId))}
        let execChanged = false
        for (const id of toRemove) {
            if (id in execMap) {
                delete execMap[id]
                execChanged = true
            }
        }
        if (execChanged) {
            set(executionByMessageIdAtomFamily(loadableId), execMap)
        }

        // Sync to entity drafts
        set(syncChatMessagesToEntityAtom, loadableId)
    },
)

/**
 * Clear all response messages for a session after a given user message.
 *
 * Used for re-runs: removes old assistant/tool responses for the session
 * that were triggered by (or after) the specified user message.
 */
export const clearSessionResponsesAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & ClearSessionResponsesPayload) => {
        const {loadableId, sessionId, afterUserMessageId} = payload
        const ids = get(messageIdsAtomFamily(loadableId))
        const byId = get(messagesByIdAtomFamily(loadableId))

        // Find the position of the user message
        const userIdx = ids.indexOf(afterUserMessageId)
        if (userIdx < 0) return

        // Collect IDs to remove: session-owned messages after (and responding to) this user message
        const toRemove: string[] = []
        for (let i = userIdx + 1; i < ids.length; i++) {
            const msg = byId[ids[i]]
            if (!msg) continue
            if (msg.sessionId === sessionId) {
                toRemove.push(ids[i])
            }
        }

        if (toRemove.length > 0) {
            set(removeMessagesAtom, {loadableId, messageIds: toRemove})
        }
    },
)

/**
 * Clear all messages for a loadable.
 */
export const clearAllMessagesAtom = atom(null, (_get, set, payload: {loadableId: string}) => {
    const {loadableId} = payload
    set(messageIdsAtomFamily(loadableId), [])
    set(messagesByIdAtomFamily(loadableId), {})
    set(executionByMessageIdAtomFamily(loadableId), {})
})

// ============================================================================
// EXECUTION LIFECYCLE
// ============================================================================

/**
 * Mark a response message as complete.
 */
export const completeMessageExecutionAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & CompleteExecutionPayload) => {
        const {loadableId, messageId, result} = payload
        const execMap = get(executionByMessageIdAtomFamily(loadableId))
        const existing = execMap[messageId]

        set(executionByMessageIdAtomFamily(loadableId), {
            ...execMap,
            [messageId]: {
                ...existing,
                status: "complete",
                output: result.output,
                traceId: result.traceId,
                resultHash: result.resultHash,
                completedAt: Date.now(),
            },
        })
    },
)

/**
 * Mark a response message as failed.
 */
export const failMessageExecutionAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & FailExecutionPayload) => {
        const {loadableId, messageId, error} = payload
        const execMap = get(executionByMessageIdAtomFamily(loadableId))
        const existing = execMap[messageId]

        set(executionByMessageIdAtomFamily(loadableId), {
            ...execMap,
            [messageId]: {
                ...existing,
                status: "error",
                error,
                completedAt: Date.now(),
            },
        })
    },
)

// ============================================================================
// DOMAIN-LEVEL ACTIONS (turn-aware operations)
// ============================================================================

/**
 * Add a user message to the conversation.
 * Builds a ChatMessage with SHARED_SESSION_ID and appends via addMessageAtom.
 */
export const addUserMessageAtom = atom(
    null,
    (_get, set, payload: {loadableId: string} & AddUserMessagePayload) => {
        const {loadableId, userMessage, id} = payload
        const msgId = id ?? generateMessageId()
        const userMsg: ChatMessage = {
            ...(userMessage ?? buildUserMessage()),
            id: msgId,
            sessionId: SHARED_SESSION_ID,
        }

        set(addMessageAtom, {loadableId, message: userMsg})
        return msgId
    },
)

/**
 * Truncate chat after a given turn (inclusive — the specified turn and
 * its session-scoped children are kept, everything after is removed).
 */
const truncateChatAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & TruncateChatPayload) => {
        const {loadableId, afterTurnId} = payload
        const ids = get(messageIdsAtomFamily(loadableId))
        const byId = get(messagesByIdAtomFamily(loadableId))

        const turnIdx = ids.indexOf(afterTurnId)
        if (turnIdx === -1) return

        // Find the next shared message after this one
        let cutIdx = ids.length
        for (let i = turnIdx + 1; i < ids.length; i++) {
            const m = byId[ids[i]]
            if (m && m.sessionId === SHARED_SESSION_ID) {
                cutIdx = i
                break
            }
        }

        const removedIds = ids.slice(cutIdx)
        if (removedIds.length === 0) return

        set(removeMessagesAtom, {loadableId, messageIds: removedIds})
        // Note: sync happens inside removeMessagesAtom
    },
)

/**
 * Resolve a MessageTarget to a flat message ID.
 */
function resolveTargetMessageId(
    ids: string[],
    byId: Record<string, ChatMessage>,
    target: PatchMessagePayload["target"],
): string | null {
    const {turnId, kind, sessionId, toolIndex} = target

    if (kind === "user") return turnId

    if (!sessionId) return null

    if (kind === "assistant") {
        return (
            ids.find((mid) => {
                const m = byId[mid]
                return (
                    m &&
                    m.parentId === turnId &&
                    m.sessionId === sessionId &&
                    m.role === "assistant"
                )
            }) ?? null
        )
    }

    if (kind === "tool") {
        const idx = toolIndex ?? 0
        const toolMsgs = ids.filter((mid) => {
            const m = byId[mid]
            return m && m.parentId === turnId && m.sessionId === sessionId && m.role === "tool"
        })
        return toolMsgs[idx] ?? null
    }

    return null
}

/**
 * Patch a specific message by target (parentId + role + session).
 * Uses updateMessageAtom for updates and removeMessagesAtom for deletes.
 */
const patchMessageAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & PatchMessagePayload) => {
        const {loadableId, target, updater} = payload
        const ids = get(messageIdsAtomFamily(loadableId))
        const byId = get(messagesByIdAtomFamily(loadableId))

        const msgId = resolveTargetMessageId(ids, byId, target)
        if (!msgId) return

        const current = byId[msgId] as SimpleChatMessage | null
        const updated = updater(current)

        if (updated) {
            set(updateMessageAtom, {
                loadableId,
                messageId: msgId,
                updates: {...updated, id: msgId, sessionId: byId[msgId].sessionId},
            })
        } else {
            set(removeMessagesAtom, {loadableId, messageIds: [msgId]})
        }
    },
)

/**
 * Delete a specific message by target.
 *
 * For **shared messages** (user turns): removes the message and everything
 * after it — later turns are invalid without the deleted context. A fresh
 * blank user message is appended so the user can continue typing.
 *
 * For **session-scoped messages** (assistant/tool in comparison mode): only
 * removes messages from the SAME session from this position onward,
 * preserving other entities' responses and shared user messages.
 */
const deleteMessageAtom = atom(
    null,
    (get, set, payload: {loadableId: string} & DeleteMessagePayload) => {
        const {loadableId, target} = payload
        const ids = get(messageIdsAtomFamily(loadableId))
        const byId = get(messagesByIdAtomFamily(loadableId))

        const msgId = resolveTargetMessageId(ids, byId, target)
        if (!msgId) return

        const idx = ids.indexOf(msgId)
        if (idx < 0) return

        const deletedMsg = byId[msgId]
        const isSessionScoped = deletedMsg && deletedMsg.sessionId !== SHARED_SESSION_ID

        if (isSessionScoped) {
            // Session-scoped delete (comparison mode safe):
            // Only remove messages from the same session at or after this position.
            // Shared user messages and other entities' responses are preserved.
            const sessionId = deletedMsg.sessionId
            const toRemove = ids.slice(idx).filter((mid) => {
                const m = byId[mid]
                return m && m.sessionId === sessionId
            })
            set(removeMessagesAtom, {loadableId, messageIds: toRemove})

            // Check if this session's last remaining message is an assistant
            // with tool_calls — if so, re-create blank tool response messages.
            const remaining = get(messageIdsAtomFamily(loadableId))
            const remainingById = get(messagesByIdAtomFamily(loadableId))
            let lastSessionMsg: ChatMessage | undefined
            for (let i = remaining.length - 1; i >= 0; i--) {
                const m = remainingById[remaining[i]]
                if (m && m.sessionId === sessionId) {
                    lastSessionMsg = m
                    break
                }
            }

            if (
                lastSessionMsg &&
                lastSessionMsg.role === "assistant" &&
                Array.isArray(lastSessionMsg.tool_calls) &&
                lastSessionMsg.tool_calls.length > 0
            ) {
                const toolMsgs: ChatMessage[] = lastSessionMsg.tool_calls.map((tc, i) => ({
                    id: generateMessageId(),
                    role: "tool",
                    name: tc.function?.name || `tool_${i + 1}`,
                    tool_call_id: tc.id,
                    content: "",
                    sessionId,
                    ...(lastSessionMsg.parentId ? {parentId: lastSessionMsg.parentId} : {}),
                }))
                for (const toolMsg of toolMsgs) {
                    set(addMessageAtom, {loadableId, message: toolMsg})
                }
            }
        } else {
            // Shared message delete: truncate everything from this position onward
            const toRemove = ids.slice(idx)
            set(removeMessagesAtom, {loadableId, messageIds: toRemove})

            // Decide what to append after truncation:
            // - If the tail is a user message → nothing (user can continue typing)
            // - If the tail is an assistant with tool_calls → re-create blank tool
            //   response messages so the user can fill them in
            // - Otherwise → append a blank user message
            const remaining = get(messageIdsAtomFamily(loadableId))
            const remainingById = get(messagesByIdAtomFamily(loadableId))
            const last = remainingById[remaining[remaining.length - 1]]
            const tailIsUserMsg =
                last && last.sessionId === SHARED_SESSION_ID && last.role === "user"

            if (tailIsUserMsg) {
                // User message is already the natural next input — nothing to add
            } else if (
                last &&
                last.role === "assistant" &&
                Array.isArray(last.tool_calls) &&
                last.tool_calls.length > 0
            ) {
                // Re-create blank tool response messages for each tool call
                const toolMsgs: ChatMessage[] = last.tool_calls.map((tc, i) => ({
                    id: generateMessageId(),
                    role: "tool",
                    name: tc.function?.name || `tool_${i + 1}`,
                    tool_call_id: tc.id,
                    content: "",
                    sessionId: last.sessionId,
                    ...(last.parentId ? {parentId: last.parentId} : {}),
                }))
                for (const toolMsg of toolMsgs) {
                    set(addMessageAtom, {loadableId, message: toolMsg})
                }
            } else {
                set(addMessageAtom, {
                    loadableId,
                    message: {
                        id: generateMessageId(),
                        role: "user",
                        content: "",
                        sessionId: SHARED_SESSION_ID,
                    },
                })
            }
        }
    },
)

// ============================================================================
// CONTEXT-AWARE ACTIONS (auto-inject loadableId from primary node)
// ============================================================================

export const addUserMessageWithContextAtom = atom(
    null,
    (get, set, payload: AddUserMessagePayload) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return null
        return set(addUserMessageAtom, {...payload, loadableId})
    },
)

export const truncateChatWithContextAtom = atom(null, (get, set, payload: TruncateChatPayload) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return
    set(truncateChatAtom, {...payload, loadableId})
})

export const patchMessageWithContextAtom = atom(null, (get, set, payload: PatchMessagePayload) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return
    set(patchMessageAtom, {...payload, loadableId})
})

export const deleteMessageWithContextAtom = atom(
    null,
    (get, set, payload: DeleteMessagePayload) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return
        set(deleteMessageAtom, {...payload, loadableId})
    },
)

export const addMessageWithContextAtom = atom(null, (get, set, payload: AddMessagePayload) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return null
    return set(addMessageAtom, {...payload, loadableId})
})

export const clearSessionResponsesWithContextAtom = atom(
    null,
    (get, set, payload: ClearSessionResponsesPayload) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return
        set(clearSessionResponsesAtom, {...payload, loadableId})
    },
)

export const clearAllMessagesWithContextAtom = atom(null, (get, set) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return
    set(clearAllMessagesAtom, {loadableId})
})
